import type { ScenarioStage } from "./incidents";

export type DiveStage = "diagnose" | "integrate" | "validate" | "enable";

export type DiveStageDefinition = {
  id: DiveStage;
  label: string;
  objective: string;
  completionSignal: string;
};

export const DIVE_STAGE_DEFINITIONS: Record<DiveStage, DiveStageDefinition> = {
  diagnose: {
    id: "diagnose",
    label: "Diagnose",
    objective: "Establish impact, scope, and trustworthy evidence.",
    completionSignal: "The incident has enough linked evidence to explain the likely fault.",
  },
  integrate: {
    id: "integrate",
    label: "Integrate",
    objective: "Connect changes, infrastructure, ownership, people, and services.",
    completionSignal: "The proposed response is grounded in the complete incident path.",
  },
  validate: {
    id: "validate",
    label: "Validate",
    objective: "Check readiness and require an accountable human decision.",
    completionSignal: "Blocked checks are cleared and warnings have explicit override receipts.",
  },
  enable: {
    id: "enable",
    label: "Enable",
    objective: "Verify recovery, communicate clearly, and preserve follow-up work.",
    completionSignal: "Service and endpoint recovery are verified and closure artifacts are recorded.",
  },
};

export const DIVE_STAGE_BY_SCENARIO_STAGE: Record<ScenarioStage, DiveStage> = {
  calm: "diagnose",
  detected: "diagnose",
  investigating: "diagnose",
  identified: "integrate",
  mitigating: "validate",
  monitoring: "enable",
  recovered: "enable",
};

export type SnapshotProvenanceKind =
  | "deterministic-campus-data"
  | "current-scenario-state"
  | "derived-local-metric"
  | "demonstration-target"
  | "coming-soon-external-integration";

export type SnapshotProvenance = {
  kind: SnapshotProvenanceKind;
  label: string;
};

export type SnapshotStatus = "healthy" | "warning" | "critical" | "neutral";

export type OperatingSnapshotMetric = {
  id: string;
  label: string;
  value: number | string;
  unit?: string;
  status: SnapshotStatus;
  provenance: SnapshotProvenance;
  observedAt: string;
  anchorIds: readonly string[];
};

export type OperatingSnapshot = {
  serviceSla: {
    availability: OperatingSnapshotMetric;
    target: OperatingSnapshotMetric;
  };
  voice: {
    registered: OperatingSnapshotMetric;
    affectedAnchor: OperatingSnapshotMetric;
  };
  wifi: {
    coverage: OperatingSnapshotMetric;
    affectedAreas: OperatingSnapshotMetric;
  };
  switchHealth: {
    state: OperatingSnapshotMetric;
    lastCheck: OperatingSnapshotMetric;
  };
};

export type ReadinessStatus = "pass" | "warning" | "blocked";

export type ReadinessRuleId =
  | "incident-acknowledged"
  | "owner-assigned"
  | "evidence-sufficient"
  | "redundancy-known"
  | "maintenance-window"
  | "human-approver";

export type OverridableReadinessRuleId = "redundancy-known" | "maintenance-window";

export type ReadinessResult = {
  ruleId: ReadinessRuleId;
  label: string;
  status: ReadinessStatus;
  reason: string;
  nextAction: string;
  canOverride: boolean;
};

export type RedundancyState = "available" | "limited" | "unavailable" | "unknown";
export type MaintenanceWindowState = "open" | "outside-window" | "unknown";

export type ReadinessFacts = {
  acknowledgedBy: string | null;
  ownerId: string | null;
  linkedEvidenceCount: number;
  requiredEvidenceCount: number;
  redundancyState: RedundancyState;
  maintenanceWindow: MaintenanceWindowState;
  approver: {
    id: string;
    name: string;
    canApproveSensitiveChanges: boolean;
  } | null;
};

export type ReadinessOverride = {
  ruleId: OverridableReadinessRuleId;
  actor: string;
  reason: string;
};

export type ReadinessSummary = {
  results: readonly ReadinessResult[];
  blockedCount: number;
  warningCount: number;
  unresolvedWarningCount: number;
  confirmable: boolean;
};

type ScenarioInteractionBase = {
  actor: string;
  incidentId: "INC-2048";
  reason: string;
};

export type ScenarioInteractionEvent =
  | (ScenarioInteractionBase & {
      type: "incident-acknowledged";
    })
  | (ScenarioInteractionBase & {
      type: "owner-assigned";
      ownerId: string;
      ownerName: string;
    })
  | (ScenarioInteractionBase & {
      type: "readiness-override-recorded";
      ruleId: OverridableReadinessRuleId;
    })
  | (ScenarioInteractionBase & {
      type: "response-decided";
      decision: "approved" | "rejected";
      responseId: "rollback" | "reroute" | "observe";
      approverId: string;
      approverName: string;
    })
  | (ScenarioInteractionBase & {
      type: "recovery-verified";
      serviceRecovered: boolean;
      endpointId: "TEL-0318";
      endpointReregistered: boolean;
    });

export type HumanResponseDecision = {
  decision: "approved" | "rejected";
  responseId: "rollback" | "reroute" | "observe";
  approverId: string;
  approverName: string;
};

export type RecoveryVerification = {
  serviceRecovered: boolean;
  endpointId: "TEL-0318";
  endpointReregistered: boolean;
  verifiedBy: string;
};
